import { ShieldCheck } from "lucide-react";

export type SecurityNonAffiliationNoticeProps = {
  /**
   * The specific invitation, product, organisation, or activity covered by the
   * notice. Require an explicit subject so the statement is never ambiguous.
   */
  subject: string;
  /** Optional contextual explanation of the editorial purpose. */
  purpose?: string;
};

/**
 * A reusable, opt-in editorial notice for evidence-led scam, phishing and
 * security-warning articles. Keep it visible rather than embedding it in
 * Markdown so it remains consistently accessible and easy to audit.
 */
export default function SecurityNonAffiliationNotice({
  subject,
  purpose = "This article is independent editorial guidance to help clients and readers verify unexpected requests through official channels before granting access.",
}: SecurityNonAffiliationNoticeProps) {
  return (
    <aside
      className="security-non-affiliation-notice rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-sm leading-relaxed text-foreground"
      role="note"
      aria-labelledby="security-non-affiliation-heading"
      data-testid="security-non-affiliation-notice"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        <div>
          <h2 id="security-non-affiliation-heading" className="font-semibold text-foreground">
            Independent editorial notice
          </h2>
          <p className="mt-1.5 text-muted-foreground">
            Integrated.Social is not associated with, affiliated with, or endorsing {subject}. We did not create, distribute or test the item or activity discussed.
          </p>
          <p className="mt-2 text-muted-foreground">{purpose}</p>
        </div>
      </div>
    </aside>
  );
}
